import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  colors,
  DialogOverlay,
  DialogOverlayActions,
  H3,
  IconButton,
  List,
  Row,
  Separator,
  useOverlay,
  type UseOverlayReturnOptions,
} from '@fs/zion-ui'
import { NoticeInfo } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { type GroupMember, type TreeGroupDetails } from '@fs/zion-tree-types'
import MemberItem from '../../components/details/MemberItem'

const contributorItemCss = css`
  padding: 0px 10px;

  [class*='innerContainerCss'] {
    padding: 13px 0px;
    padding-inline-start: 15px;
    background-color: ${colors.gray02};
    border-radius: 6px;
  }
`

type ContributorRowProps = {
  title: string
  members: GroupMember[]
  adminCount?: number
  hideAdminTag?: boolean
  permissionsTitle: string
  permissionsParagraph: string
  group: TreeGroupDetails
}

export default function ContributorRow({
  title,
  members,
  adminCount,
  hideAdminTag,
  permissionsTitle,
  permissionsParagraph,
  group,
}: ContributorRowProps): JSX.Element {
  const permissionsOverlay = useOverlay()

  return (
    <>
      <Row alignY="middle" guttersX="nano">
        <H3 size="H6">{title}</H3>
        <IconButton
          data-testid="notice-info-button"
          Icon={NoticeInfo}
          size="xs"
          title={permissionsTitle}
          onClick={() => permissionsOverlay.open()}
        />
      </Row>
      <Separator size="xxs" />
      <div css={contributorItemCss}>
        <List>
          {members?.map((member) => (
            <React.Fragment key={member.memberCisId}>
              <MemberItem member={member} hideAdminTag={hideAdminTag} adminCount={adminCount} group={group} />
              <Separator size="xxs" />
            </React.Fragment>
          ))}
        </List>
      </div>
      <Separator size="sm" />
      <PermissionsDialog title={permissionsTitle} paragraph={permissionsParagraph} overlay={permissionsOverlay} />
    </>
  )
}

type PermissionsDialogProps = {
  title: string
  paragraph: string
  overlay: UseOverlayReturnOptions
}

function PermissionsDialog({ title, paragraph, overlay }: PermissionsDialogProps): JSX.Element {
  const [t] = useTranslation()
  return (
    <DialogOverlay
      title={title}
      footer={
        <DialogOverlayActions>
          <Button data-testid="" onClick={overlay.close}>
            {t('group-management.manage-contributors.permissions-action')}
          </Button>
        </DialogOverlayActions>
      }
      {...overlay}
    >
      {paragraph}
    </DialogOverlay>
  )
}
